<template>
	<div
		class="g-block-title-underline g-block-title-padding"
		:class="{
			'g-block-title-underline--edit': editable,
			'is-active': editable && active,
			'g-block-title-underline--h5': h5,
			'g-block-title-padding--h5': h5,
		}"
		@click="onWrapClick"
	>
		<div class="g-block-title-underline__inner" :style="{ color: themeColor }">
			<template v-if="editable">
				<div class="g-block-title-underline__edit-box">
					<span
						ref="firstCharMeasure"
						class="g-block-title-underline__measure"
						aria-hidden="true"
					>{{ firstChar }}</span>
					<RichTextEditable
						class="g-block-title-underline__text"
						fit-content
						:model-value="data.content"
						placeholder="请输入标题"
						@update:model-value="onContentUpdate"
						@focus="onWrapClick"
					/>
					<span
						v-if="firstChar"
						class="g-block-title-underline__line"
						:style="{ width: lineWidth }"
					></span>
				</div>
			</template>
			<template v-else>
				<span v-if="firstChar" class="g-block-title-underline__first">
					<span class="g-block-title-underline__first-text">{{ firstChar }}</span>
					<span class="g-block-title-underline__line"></span>
				</span>
				<span v-if="restText" class="g-block-title-underline__rest">{{ restText }}</span>
			</template>
		</div>
	</div>
</template>

<script>
	import RichTextEditable from '../../../components/RichTextEditable.vue';
	import { getTitleThemeColor } from '../../../templates.js';

	function stripHtml(html) {
		if (!html) return '';
		return String(html)
			.replace(/<[^>]+>/g, '')
			.replace(/&nbsp;/g, ' ')
			.trim();
	}

	export default {
		name: 'GBlockTitleUnderline',
		components: { RichTextEditable },
		props: {
			data: {
				type: Object,
				required: true,
			},
			editable: {
				type: Boolean,
				default: false,
			},
			active: {
				type: Boolean,
				default: false,
			},
			h5: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['select', 'update'],
		data() {
			return {
				lineWidthPx: 0,
			};
		},
		computed: {
			themeColor() {
				return getTitleThemeColor(this.data);
			},
			plainText() {
				return stripHtml(this.data.content);
			},
			firstChar() {
				return this.plainText.charAt(0) || '';
			},
			restText() {
				return this.plainText.slice(1);
			},
			lineWidth() {
				return this.lineWidthPx > 0 ? `${this.lineWidthPx}px` : '1em';
			},
		},
		watch: {
			firstChar() {
				this.$nextTick(this.measureLineWidth);
			},
			'data.content'() {
				this.$nextTick(this.measureLineWidth);
			},
		},
		mounted() {
			this.measureLineWidth();
		},
		updated() {
			if (this.editable) {
				this.$nextTick(this.measureLineWidth);
			}
		},
		methods: {
			measureLineWidth() {
				const el = this.$refs.firstCharMeasure;
				this.lineWidthPx = el ? el.offsetWidth : 0;
			},
			onWrapClick(e) {
				if (!this.editable) return;
				e?.stopPropagation?.();
				this.$emit('select');
			},
			onContentUpdate(content) {
				this.$emit('update', { content });
			},
		},
	};
</script>

<style scoped lang="less">
	@line-height: 3px;
	@line-gap: 8px;
	@font-size: 24px;

	.g-block-title-underline {
		&--edit {
			border: 1px solid transparent;
			border-radius: 4px;
			transition: border-color 0.2s, box-shadow 0.2s;
			cursor: pointer;

			&.is-active {
				border-color: #285af9;
				box-shadow: 0 0 0 1px rgba(40, 90, 249, 0.15);
			}
		}
	}

	.g-block-title-underline__inner {
		display: flex;
		align-items: baseline;
		flex-wrap: wrap;
		text-align: left;
	}

	.g-block-title-underline__edit-box {
		position: relative;
		display: inline-block;
		max-width: 100%;
		font-size: @font-size;
		font-weight: 700;
		line-height: 1.2;
	}

	.g-block-title-underline__measure {
		position: absolute;
		visibility: hidden;
		pointer-events: none;
		font-size: @font-size;
		font-weight: 700;
		line-height: 1.2;
		white-space: pre;
	}

	.g-block-title-underline__first {
		position: relative;
		display: inline-block;
		flex-shrink: 0;
	}

	.g-block-title-underline__first,
	.g-block-title-underline__first-text,
	.g-block-title-underline__text,
	.g-block-title-underline__rest {
		font-size: @font-size;
		font-weight: 700;
		line-height: 1.2;
		word-break: break-word;
		white-space: pre-wrap;
	}

	.g-block-title-underline__first,
	.g-block-title-underline__first-text {
		display: inline-block;
	}

	.g-block-title-underline__line {
		display: block;
		width: 100%;
		height: @line-height;
		margin-top: @line-gap;
		background: currentColor;
		border-radius: 1px;
	}

	.g-block-title-underline__edit-box .g-block-title-underline__line {
		position: absolute;
		left: 0;
		bottom: 0;
		margin-top: 0;
	}

	.g-block-title-underline__text {
		display: inline-block;
		padding: 0;
		padding-bottom: @line-height + @line-gap;
		font-family: inherit;
		color: inherit;

		:deep(.g-rich-text-editable) {
			display: inline-block;
			width: auto;
			max-width: 100%;
			color: inherit;
		}

		:deep(.g-rich-text-editable__content) {
			line-height: 1.2;
			min-height: unset;
			height: auto;
			padding: 0;
			color: inherit;
			font-weight: 700;
		}

		:deep(span),
		:deep(font),
		:deep(p),
		:deep(div) {
			line-height: inherit;
			margin: 0;
			padding: 0;
			vertical-align: baseline;
			color: inherit;
			font-weight: inherit;
		}
	}

	.g-block-title-underline--h5 {
		@h5-line-height: 0.06rem;
		@h5-line-gap: 0.16rem;
		@h5-font-size: 0.48rem;

		.g-block-title-underline__edit-box {
			font-size: @h5-font-size;
		}

		.g-block-title-underline__measure {
			font-size: @h5-font-size;
		}

		.g-block-title-underline__line {
			height: @h5-line-height;
			margin-top: @h5-line-gap;
		}

		.g-block-title-underline__edit-box .g-block-title-underline__line {
			margin-top: 0;
		}

		.g-block-title-underline__first,
		.g-block-title-underline__first-text,
		.g-block-title-underline__text,
		.g-block-title-underline__rest {
			font-size: @h5-font-size;
		}

		.g-block-title-underline__text {
			padding-bottom: @h5-line-height + @h5-line-gap;
		}
	}
</style>
